import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Box from '@mui/material/Box';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import { tableStyles } from './styled';

export interface SortProps {
    handleSort: (sortBy: string) => void;
    sortBy: string;
    sort: 'asc' | 'desc';
}

export interface PaginatedTableProps<T> {
    data: T[];
    totalCount: number;
    currentPage: number;
    rowsPerPage: number;
    onPageChange: (page: number) => Promise<void>;
    onRowsPerPageChange: (rowsPerPage: number) => void;
    sortComparator: (
        a: T,
        b: T,
        sortBy: string,
        direction: 'asc' | 'desc'
    ) => number;
    renderHeader: (props: SortProps) => ReactNode;
    renderRow: (item: T, index: number) => ReactNode;
    ariaLabel?: string;
}

function PaginatedTable<T>({
    data,
    totalCount,
    currentPage,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    sortComparator,
    renderHeader,
    renderRow,
    ariaLabel = 'table'
}: PaginatedTableProps<T>) {
    const [sortBy, setSortBy] = useState<string>('');
    const [sort, setSort] = useState<'asc' | 'desc'>('asc');
    const [sortedData, setSortedData] = useState<T[]>([]);
    const boxRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (data) {
            setSortedData(data);
        }
    }, [data]);

    const scrollToTable = useCallback(() => {
        if (boxRef?.current) {
            const yOffset = -100;
            const element = boxRef.current;
            const y =
                element.getBoundingClientRect().top +
                window.pageYOffset +
                yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, []);

    const handleChangePage = useCallback(
        (
            _event: React.MouseEvent<HTMLButtonElement> | null,
            pageChanged: number
        ) => {
            onPageChange(pageChanged).then(() => {
                setTimeout(() => {
                    scrollToTable();
                }, 100);
            });
        },
        [onPageChange, scrollToTable]
    );

    const handleChangeRowsPerPage = useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const newRowsPerPage = parseInt(event.target.value);
            onRowsPerPageChange(newRowsPerPage);
            setTimeout(() => {
                scrollToTable();
            }, 100);
        },
        [onRowsPerPageChange, scrollToTable]
    );

    const handleSort = useCallback(
        (sortedBy: string) => {
            setSortBy(sortedBy);
            const sortDirection = sort === 'asc' ? 'desc' : 'asc';
            setSort(sortDirection);

            const sorted = [...sortedData].sort((a, b) =>
                sortComparator(a, b, sortedBy, sortDirection)
            );
            setSortedData(sorted);
        },
        [sortedData, sort, sortComparator]
    );

    const items = sortedData || [];

    return (
        <Box ref={boxRef}>
            <TableContainer component={Paper} sx={tableStyles.container}>
                <Table
                    stickyHeader
                    sx={tableStyles.table}
                    aria-label={ariaLabel}
                >
                    {renderHeader({ handleSort, sortBy, sort })}
                    <TableBody style={tableStyles.body}>
                        {items.map((item, i) => renderRow(item, i))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                sx={{
                    ...tableStyles.pagination,
                    ...tableStyles.paginationLabel,
                    ' svg': tableStyles.caret,
                    '& .MuiTablePagination-select': tableStyles.paginationSelect
                }}
                count={totalCount}
                page={currentPage}
                rowsPerPageOptions={[5, 10, 20, 50, 100]}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Box>
    );
}

export default PaginatedTable;
